import React, { useState, useEffect, useRef } from "react";
import { FaUserAlt, FaQuoteLeft } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

const TestimonialSkeleton = () => {
  return (
    <div className="flex gap-4 mb-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-6 animate-pulse border border-white/20 shadow-xl">
      <div className="w-16 h-16 bg-white/20 rounded-xl flex-shrink-0"></div>
      <div className="flex-1 space-y-3">
        <div className="space-y-2">
          <div className="h-4 bg-white/20 rounded-lg w-32"></div>
          <div className="h-3 bg-white/20 rounded-lg w-24"></div>
        </div>
        <div className="space-y-2 pt-2">
          <div className="h-3 bg-white/20 rounded-lg w-full"></div>
          <div className="h-3 bg-white/20 rounded-lg w-5/6"></div>
          <div className="h-3 bg-white/20 rounded-lg w-4/6"></div>
        </div>
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    saveInfo: false,
  });

  const [testimonials, setTestimonials] = useState([]);
  const [visibleTestimonials, setVisibleTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      mirror: true,
      easing: "ease-out",
    });

    const handleScroll = () => {
      AOS.refresh();
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (testimonials.length > 3 && !isHovered) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % testimonials.length;
          return nextIndex;
        });
      }, 3000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [testimonials.length, isHovered]);

  useEffect(() => {
    if (testimonials.length > 0) {
      if (testimonials.length <= 3) {
        setVisibleTestimonials(testimonials);
      } else {
        const visible = [];
        for (let i = 0; i < 3; i++) {
          const index = (currentIndex + i) % testimonials.length;
          visible.push(testimonials[index]);
        }
        setVisibleTestimonials(visible);
      }
    }
  }, [currentIndex, testimonials]);

  const fetchTestimonials = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/testimonial/all`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      setTestimonials(data.reverse());
      if (data.length > 0) {
        if (data.length <= 3) {
          setVisibleTestimonials(data);
        } else {
          const initial = data.slice(0, 3);
          setVisibleTestimonials(initial);
        }
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/testimonial/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user: {
              name: `${formData.firstName} ${formData.lastName}`.trim(),
              email: formData.email,
            },
            message: formData.message,
          }),
        }
      );

      if (response.ok) {
        const newTestimonial = await response.json();
        console.log("Testimonial submitted:", newTestimonial);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
          saveInfo: false,
        });
        fetchTestimonials();
      } else {
        console.error("Error submitting testimonial");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="mb-12 tracking-wide bg-gradient-to-br from-primaryBtnHoverColor via-primaryBtnColor to-primaryTextColor py-12 w-full px-4 md:px-8 lg:px-12 mx-auto min-h-screen">
      <div className="">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            Testimonials
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Discover what our clients say about their experience with us
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* Left Side - Testimonials */}
          <div className="lg:pr-6" data-aos="fade-left">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <FaQuoteLeft className="text-blue-400" />
                What People Say
              </h2>
              <div
                className="space-y-6 overflow-y-auto max-h-[600px] pr-4 custom-scrollbar"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {isLoading ? (
                  <>
                    <TestimonialSkeleton />
                    <TestimonialSkeleton />
                    <TestimonialSkeleton />
                  </>
                ) : visibleTestimonials.length === 0 ? (
                  <div className="text-center py-12 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8">
                    <FaUserAlt className="size-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-300 text-lg mb-2">
                      No testimonials yet.
                    </p>
                    <p className="text-gray-400">
                      Be the first to share your experience!
                    </p>
                  </div>
                ) : (
                  visibleTestimonials.map((testimonial, index) => (
                    <div
                      key={`${
                        testimonial._id || testimonial.user?.name
                      }-${index}`}
                      className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-white/30 transition-all duration-300 hover:shadow-xl group"
                    >
                      <div className="flex gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center flex-shrink-0 border border-white/30 group-hover:border-white/50 transition-all duration-300">
                          <FaUserAlt className="size-7 text-white/80" />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col gap-1 mb-3">
                            <h4 className="text-lg capitalize font-semibold text-white group-hover:text-blue-200 transition-colors duration-300">
                              {testimonial.user?.name}
                            </h4>
                            {/* <span className="text-xs text-gray-400">
                              {testimonial.user?.email}
                            </span> */}
                          </div>
                          <p className="text-gray-200 text-sm leading-relaxed italic">
                            "{testimonial.message}"
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:pl-6" data-aos="fade-right">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl sticky top-8">
              <h3 className="text-2xl font-bold text-white mb-2 text-center">
                Share Your Experience
              </h3>
              <p className="text-gray-300 text-center mb-8">
                Help others by sharing your story
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-semibold text-gray-200 mb-3"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 text-white placeholder-gray-400 transition-all duration-200 backdrop-blur-sm"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-semibold text-gray-200 mb-3"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 text-white placeholder-gray-400 transition-all duration-200 backdrop-blur-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-200 mb-3"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 text-white placeholder-gray-400 transition-all duration-200 backdrop-blur-sm"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-gray-200 mb-3"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 text-white placeholder-gray-400 resize-vertical transition-all duration-200 backdrop-blur-sm"
                    placeholder="Share your experience with us..."
                  />
                </div>
                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-primaryTextColor via-primaryBtnColor to-primaryBtnHoverColor hover:from-primaryBtnHoverColor hover:via-primaryBtnColor hover:to-primaryTextColor tracking-wide font-bold px-8 py-4 rounded-xl transition-all duration-300 uppercase text-sm text-white shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 w-full max-w-md group"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Post Testimonial
                      <svg
                        className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default TestimonialsSection;